/**
 * Palm Oracle Reading Result Page
 * Refactored for maintainability - Phase 3 QA Release
 * 
 * Components extracted:
 * - ReadingHeader
 * - PalmOverlaySection
 * - InterpretationSection
 * - OverallPatternCard
 * - ConfidenceBadge
 * - ExplainabilityModal
 * - CinematicReveal
 * - ReadingFooter
 * - DNACardsGrid (existing)
 * - PalmOverlay (existing)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, Hand, Loader2, AlertCircle, RefreshCw,
  Heart, Brain, Briefcase, Star, Eye, Sparkles, AlertTriangle
} from 'lucide-react';

// API
import { getReadingResult, getReadingStatus, STATUS_LABELS } from '../services/palmApi';

// Components
import PalmOverlay from '../components/PalmOverlay';
import { DNACardsGrid } from '../components/DNACard';
import ConfidenceBadge from '../components/ConfidenceBadge';
import ExplainabilityModal from '../components/ExplainabilityModal';
import CinematicReveal from '../components/CinematicReveal';
import OverallPatternCard from '../components/OverallPatternCard';
import InterpretationSection from '../components/InterpretationSection';
import ReadingFooter from '../components/ReadingFooter';

// Styles
import '../styles/palm.css';

// ============ CONSTANTS ============

const SECTION_ICONS = {
  general_story: Sparkles,
  relationships: Heart,
  mind_decisions: Brain,
  life_energy: Star,
  career_direction: Briefcase,
  communication_social: Eye,
  creativity_intuition: Eye,
  hidden_power: Star,
  growth_area: Star,
  daily_message: Sparkles,
  limitations: AlertTriangle
};

const LINE_COLORS = {
  heart: '#ef4444',
  head: '#3b82f6',
  life: '#22c55e',
  fate: '#a855f7',
  sun: '#f59e0b',
  mercury: '#6366f1'
};

const SECTION_TO_LINE = {
  relationships: 'heart',
  mind_decisions: 'head',
  life_energy: 'life',
  career_direction: 'fate',
  creativity_intuition: 'sun',
  communication_social: 'mercury'
};

const LINE_NAMES = {
  heart: { tr: 'Kalp Çizgisi', en: 'Heart Line' },
  head: { tr: 'Akıl Çizgisi', en: 'Head Line' },
  life: { tr: 'Yaşam Çizgisi', en: 'Life Line' },
  fate: { tr: 'Kader Çizgisi', en: 'Fate Line' },
  sun: { tr: 'Güneş Çizgisi', en: 'Sun Line' },
  mercury: { tr: 'Merkür Çizgisi', en: 'Mercury Line' }
};

const SECTION_TITLES = {
  general_story: { tr: 'Avuç İçinin Genel Hikâyesi', en: 'General Story of Your Palm' },
  relationships: { tr: 'Kalp ve İlişkiler', en: 'Heart and Relationships' },
  mind_decisions: { tr: 'Zihin ve Karar Verme', en: 'Mind and Decision Making' },
  life_energy: { tr: 'Yaşam Enerjisi ve Dayanıklılık', en: 'Life Energy and Resilience' },
  career_direction: { tr: 'Kariyer ve Yön', en: 'Career and Direction' },
  communication_social: { tr: 'İletişim ve Sosyal Güç', en: 'Communication and Social Strength' },
  creativity_intuition: { tr: 'Yaratıcılık ve Sezgi', en: 'Creativity and Intuition' },
  hidden_power: { tr: 'Gizli Güç', en: 'Hidden Power' },
  growth_area: { tr: 'Gelişim Alanı', en: 'Growth Area' },
  daily_message: { tr: 'Bugünün Sembolik Mesajı', en: "Today's Symbolic Message" },
  limitations: { tr: 'Analiz Sınırlamaları', en: 'Analysis Limitations' }
};

const getLineName = (lineType, lang) => LINE_NAMES[lineType]?.[lang] || lineType;

// ============ MAIN COMPONENT ============

const PalmReadingPage = ({ token, credits = 0, onCreditsUpdate, initialReadingData = null }) => {
  const { readingId } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language === 'en' ? 'en' : 'tr';
  
  // ============ STATE ============
  
  // Data fetching state
  const [loading, setLoading] = useState(!initialReadingData);
  const [error, setError] = useState(null);
  const [reading, setReading] = useState(initialReadingData);
  const [polling, setPolling] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(null);
  
  // Overlay state
  const [showOverlay, setShowOverlay] = useState(true);
  const [highlightedLine, setHighlightedLine] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  
  // Modal state
  const [explainabilityModal, setExplainabilityModal] = useState(null);
  
  // Cinematic Reveal state
  const [revealComplete, setRevealComplete] = useState(false);
  const [skipReveal, setSkipReveal] = useState(false);
  
  // ============ MEMOS ============
  
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }, []);
  
  const wasViewed = useMemo(() => {
    if (!readingId) return false;
    try {
      const viewedReadings = JSON.parse(localStorage.getItem('viewedPalmReadings') || '[]');
      return viewedReadings.includes(readingId);
    } catch {
      return false;
    }
  }, [readingId]);
  
  const overlayLines = useMemo(() => {
    if (reading?.overlay_lines?.length > 0) {
      return reading.overlay_lines.filter(line => 
        line.points && line.points.length >= 2
      );
    }
    
    const obs = reading?.structured_observation;
    if (!obs) return [];
    
    if (Array.isArray(obs.lines)) {
      return obs.lines.filter(line => 
        line.points && line.points.length >= 2 && line.confidence >= 0.3
      );
    }
    
    if (obs.lines && typeof obs.lines === 'object') {
      return Object.entries(obs.lines)
        .filter(([_, data]) => data.points && data.points.length >= 2 && data.confidence >= 0.3)
        .map(([type, data]) => ({ line_type: type, ...data }));
    }
    
    return [];
  }, [reading?.overlay_lines, reading?.structured_observation]);
  
  // ============ CALLBACKS ============
  
  const handleSectionClick = useCallback((sectionId) => {
    const lineType = SECTION_TO_LINE[sectionId];
    if (lineType) {
      setHighlightedLine(prev => prev === lineType ? null : lineType);
      setActiveSection(prev => prev === sectionId ? null : sectionId);
    }
  }, []);
  
  const handleLineClick = useCallback((lineType) => {
    setHighlightedLine(prev => prev === lineType ? null : lineType);
  }, []);
  
  const openExplainability = useCallback((section) => {
    if (section.explainability || (section.status === 'limited' && section.limitations?.length > 0)) {
      setExplainabilityModal(section);
    }
  }, []);
  
  const closeExplainability = useCallback(() => {
    setExplainabilityModal(null);
  }, []);
  
  const handleSkipReveal = useCallback(() => {
    setSkipReveal(true);
    setRevealComplete(true);
    try {
      const viewedReadings = JSON.parse(localStorage.getItem('viewedPalmReadings') || '[]');
      if (!viewedReadings.includes(readingId)) {
        viewedReadings.push(readingId);
        localStorage.setItem('viewedPalmReadings', JSON.stringify(viewedReadings.slice(-50)));
      }
    } catch (e) { /* localStorage unavailable */ }
  }, [readingId]);
  
  const loadReading = useCallback(async () => {
    if (!token || !readingId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await getReadingResult(token, readingId);
      setReading(data);
      
      if (['analyzing', 'processing', 'pending'].includes(data.status)) {
        setPolling(true);
        setPollingStatus(data.status);
      }
    } catch (err) {
      setError(err.message || 'Failed to load reading');
    } finally {
      setLoading(false);
    }
  }, [token, readingId]);
  
  // ============ EFFECTS ============
  
  // Load reading on mount
  useEffect(() => {
    if (!initialReadingData && token && readingId) {
      loadReading();
    }
  }, [token, readingId, initialReadingData, loadReading]);
  
  // Polling for processing readings
  useEffect(() => {
    if (!polling || !token || !readingId) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const data = await getReadingStatus(token, readingId);
        setPollingStatus(data.status);
        
        if (['completed', 'interpretation_failed', 'failed'].includes(data.status)) {
          setPolling(false);
          loadReading();
        }
      } catch (err) {
        setPolling(false);
        setError(err.message);
      }
    }, 3000);
    
    return () => clearInterval(pollInterval);
  }, [polling, token, readingId, loadReading]);
  
  // Cinematic reveal timer
  useEffect(() => {
    if (reading?.status === 'completed' && !wasViewed && !skipReveal && !prefersReducedMotion) {
      const timer = setTimeout(() => {
        setRevealComplete(true);
        try {
          const viewedReadings = JSON.parse(localStorage.getItem('viewedPalmReadings') || '[]');
          if (!viewedReadings.includes(readingId)) {
            viewedReadings.push(readingId);
            localStorage.setItem('viewedPalmReadings', JSON.stringify(viewedReadings.slice(-50)));
          }
        } catch (e) { /* localStorage unavailable */ }
      }, 3000);
      
      return () => clearTimeout(timer);
    } else if (wasViewed || prefersReducedMotion) {
      setRevealComplete(true);
    }
  }, [reading?.status, wasViewed, skipReveal, prefersReducedMotion, readingId]);
  
  // ============ RENDER HELPERS ============
  
  const renderLoading = () => (
    <div className="palm-loading" data-testid="palm-loading">
      <Loader2 className="animate-spin" size={48} />
      <h2>{lang === 'tr' ? 'Okuma Yükleniyor...' : 'Loading Reading...'}</h2>
    </div>
  );
  
  const renderPolling = () => (
    <div className="palm-polling" data-testid="palm-polling">
      <div className="polling-animation">
        <Hand size={64} className="polling-icon" />
        <div className="polling-rings">
          <div className="ring ring-1" />
          <div className="ring ring-2" />
          <div className="ring ring-3" />
        </div>
      </div>
      <h2>{lang === 'tr' ? 'Avuç İçiniz Analiz Ediliyor' : 'Analyzing Your Palm'}</h2>
      <p className="polling-status">
        {STATUS_LABELS[pollingStatus]?.[lang] || pollingStatus}
      </p>
      <p className="polling-info">
        {lang === 'tr' 
          ? 'Bu işlem birkaç dakika sürebilir. Lütfen sayfayı kapatmayın.'
          : 'This may take a few minutes. Please do not close this page.'
        }
      </p>
    </div>
  );
  
  const renderError = () => (
    <div className="palm-error" data-testid="palm-error">
      <AlertCircle size={48} />
      <h2>{lang === 'tr' ? 'Hata Oluştu' : 'An Error Occurred'}</h2>
      <p>{error}</p>
      <div className="error-actions">
        <button className="palm-btn palm-btn-primary" onClick={loadReading}>
          <RefreshCw size={18} />
          {lang === 'tr' ? 'Tekrar Dene' : 'Try Again'}
        </button>
        <Link to="/palm/new" className="palm-btn palm-btn-secondary">
          {lang === 'tr' ? 'Yeni Okuma Başlat' : 'Start New Reading'}
        </Link>
        <Link to="/palm" className="palm-btn palm-btn-ghost">
          {lang === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
        </Link>
      </div>
    </div>
  );
  
  const renderResult = () => {
    if (!reading) return null;
    
    const { structured_observation: obs, palm_dna: dna, quality_score, interpretation } = reading;
    
    return (
      <div className="palm-result" data-testid="palm-reading-page">
        {/* Header with Overlay */}
        <motion.div
          className="palm-result-premium-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Palm Image with Overlay */}
          <div className="palm-overlay-wrapper">
            <PalmOverlay
              lines={overlayLines}
              imageUrl={reading.image_url}
              imageWidth={reading.image_width || 400}
              imageHeight={reading.image_height || 533}
              highlightedLine={highlightedLine}
              showOverlay={showOverlay}
              lang={lang}
            />
            
            {/* Overlay Toggle & Legend */}
            {overlayLines.length > 0 && (
              <>
                <button 
                  className="palm-overlay-toggle"
                  onClick={() => setShowOverlay(!showOverlay)}
                  aria-label={showOverlay ? 'Hide overlay' : 'Show overlay'}
                  data-testid="palm-overlay-toggle"
                >
                  {showOverlay ? (
                    <>{lang === 'tr' ? 'Gizle' : 'Hide'}</>
                  ) : (
                    <>{lang === 'tr' ? 'Göster' : 'Show'}</>
                  )}
                </button>
                
                {showOverlay && (
                  <div className="palm-overlay-legend">
                    {overlayLines.map(line => (
                      <button
                        key={line.line_type}
                        className={`legend-item ${highlightedLine === line.line_type ? 'active' : ''}`}
                        onClick={() => handleLineClick(line.line_type)}
                        data-testid={`palm-legend-${line.line_type}`}
                      >
                        <span 
                          className="legend-color"
                          style={{ background: LINE_COLORS[line.line_type] }}
                        />
                        <span>{getLineName(line.line_type, lang)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Reading Info */}
          <div className="palm-result-premium-info">
            <div className="result-icon">✋</div>
            <h1>{lang === 'tr' ? 'El Falı Sonucunuz' : 'Your Palm Reading'}</h1>
            <p className="result-date">
              {new Date(reading.created_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
                year: 'numeric', month: 'long', day: 'numeric'
              })}
            </p>
            
            <div className="palm-reading-status" data-testid="palm-reading-status">
              <span className={`status-badge status-${reading.status}`}>
                {reading.status === 'completed' 
                  ? (lang === 'tr' ? 'Tamamlandı' : 'Completed')
                  : reading.status === 'interpretation_failed'
                  ? (lang === 'tr' ? 'Yorum Oluşturulamadı' : 'Interpretation Failed')
                  : reading.status
                }
              </span>
              <span className="package-badge">
                {reading.package === 'deep' 
                  ? (lang === 'tr' ? 'Derin Analiz' : 'Deep Analysis')
                  : (lang === 'tr' ? 'Standart' : 'Standard')
                }
              </span>
            </div>
          </div>
        </motion.div>
        
        {/* Interpretation Sections */}
        {interpretation?.sections && interpretation.sections.length > 0 && (
          <motion.div
            className="palm-interpretation-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <h2 className="section-title">
              <Sparkles size={20} />
              {lang === 'tr' ? 'Yorumunuz' : 'Your Interpretation'}
            </h2>
            
            <div className="interpretation-cards">
              {interpretation.sections.map((section, index) => {
                const sectionId = section.id || 'unknown';
                return (
                  <InterpretationSection
                    key={sectionId}
                    section={section}
                    index={index}
                    lang={lang}
                    isHighlighted={activeSection === sectionId}
                    hasLineMapping={!!SECTION_TO_LINE[sectionId]}
                    SectionIcon={SECTION_ICONS[sectionId]}
                    title={SECTION_TITLES[sectionId]?.[lang] || section.title_key || sectionId}
                    onSectionClick={handleSectionClick}
                    onExplainabilityClick={openExplainability}
                  />
                );
              })}
            </div>
          </motion.div>
        )}
        
        {/* Overall Pattern (Deep package only) */}
        <OverallPatternCard synthesis={interpretation?.synthesis} lang={lang} />
        
        {/* Signature Sentences */}
        {interpretation?.signature_sentences?.length > 0 && (
          <motion.div
            className="palm-signature-sentences"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h3>{lang === 'tr' ? 'Öne Çıkan Mesajlar' : 'Key Messages'}</h3>
            <div className="signature-list">
              {interpretation.signature_sentences.map((sentence, i) => (
                <div key={i} className="signature-item">
                  <span className="signature-number">{i + 1}</span>
                  <p>{sentence}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
        
        {/* DNA Cards */}
        {dna?.traits && dna.traits.length > 0 && (
          <motion.div
            className="palm-dna-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h2 className="section-title">
              <Star size={20} />
              {lang === 'tr' ? 'Palm DNA - Kişilik Haritanız' : 'Palm DNA - Your Personality Map'}
            </h2>
            <DNACardsGrid traits={dna.traits} lang={lang} />
          </motion.div>
        )}
        
        {/* Footer */}
        <ReadingFooter metrics={interpretation?.metrics} lang={lang} />
        
        {/* Navigation */}
        <div className="palm-result-actions">
          <Link to="/palm" className="palm-btn palm-btn-secondary" data-testid="palm-back-link">
            <ArrowLeft size={18} />
            {lang === 'tr' ? 'Geri Dön' : 'Go Back'}
          </Link>
          <Link to="/palm/new" className="palm-btn palm-btn-primary">
            {lang === 'tr' ? 'Yeni Okuma' : 'New Reading'}
          </Link>
        </div>
      </div>
    );
  };
  
  // ============ MAIN RENDER ============
  
  return (
    <div className="palm-reading-page">
      {loading && renderLoading()}
      {!loading && polling && renderPolling()}
      {!loading && !polling && error && renderError()}
      {!loading && !polling && !error && reading && renderResult()}
      
      {/* Explainability Modal */}
      <AnimatePresence>
        {explainabilityModal && (
          <ExplainabilityModal
            section={explainabilityModal}
            lang={lang}
            onClose={closeExplainability}
          />
        )}
      </AnimatePresence>
      
      {/* Cinematic Reveal */}
      <AnimatePresence>
        {reading?.status === 'completed' && !revealComplete && !prefersReducedMotion && (
          <CinematicReveal lang={lang} onSkip={handleSkipReveal} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PalmReadingPage;
